import axios from 'axios';

import { getCookie } from '@/lib/cookies';
import { getAxiosTimeout } from '@/lib/axios-config';

import { API_GATE_WAY } from '@/constant/routes';
import StorageKey from '@/constant/storage-key';

const baseURL = API_GATE_WAY;

const axiosWithHeaders = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: getAxiosTimeout(), // Dynamic timeout: 60s for build, 30s for runtime
});

axiosWithHeaders.interceptors.request.use((config) => {
  const token = getCookie(StorageKey.TOKEN);
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle 410 errors properly
axiosWithHeaders.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 410) {
      // Create a custom error that will be properly handled by our custom logic
      const customError = new Error(`Gone: The resource is no longer available. Status code: 410`) as any;
      customError.name = 'HttpError';
      customError.statusCode = 410;
      throw customError;
    }
    // 429 and 403 keep their AxiosError shape (client code relies on `instanceof AxiosError`), but
    // carry the status the way the 410 error does, so the error boundaries show the 429/403 page
    // instead of the generic 500 one when a page fails while rendering on the server.
    const status = error.response?.status;
    if (status === 429 || status === 403) {
      error.statusCode = status;
      error.message = `${error.message}. Status code: ${status}`;
    }
    // Handle connection errors (ECONNRESET, socket hang up, etc.)
    if (error.code === 'ECONNRESET' || error.code === 'ETIMEDOUT' || error.message?.includes('socket hang up')) {
      const connectionError = new Error(`Connection error: ${error.message || 'Network request failed'}`) as any;
      connectionError.code = error.code;
      throw connectionError;
    }
    throw error;
  }
);

export default axiosWithHeaders;
